import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Central source of truth for the AIFLedger brand assets on the client.
 *
 * The Owner uploads the wordmark ("AIFLedger" in serif navy with the two
 * gold tittle-dots) and the icon (dotted-diamond mark) via /owner/brand.
 * Every UI surface that used to render <span>AIFLedger.</span> now imports
 * <PlatformWordmark/> so a re-upload propagates everywhere with no
 * template drift.
 *
 * Priority:
 *   1. `user.platform_brand.{wordmark_url|icon_url}` (hydrated on /auth/me)
 *   2. GET /api/platform/brand (public, on-mount for logged-out surfaces)
 *   3. bundled fallbacks (/corpzo-icon-sm.png)
 *
 * Also listens for the `aif-platform-brand-updated` window event so the
 * PlatformBrand admin page can push new URLs into every mounted brand
 * without a reload.
 */

const DEFAULT_ICON = "/corpzo-icon-sm.png";

function useBrandField(field, fallback) {
  const { user } = useAuth() || {};
  const seeded = user?.platform_brand?.[field] || null;
  const [src, setSrc] = React.useState(seeded || fallback);
  const load = React.useCallback(() => {
    if (seeded) { setSrc(seeded); return; }
    api.get("/platform/brand")
      .then(({ data }) => {
        if (data?.[field]) setSrc(data[field]);
        else setSrc(fallback);
      })
      .catch(() => setSrc(fallback));
  }, [field, fallback, seeded]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const h = () => load();
    window.addEventListener("aif-platform-brand-updated", h);
    return () => window.removeEventListener("aif-platform-brand-updated", h);
  }, [load]);
  return src;
}

/** The "AIFLedger" wordmark image — used everywhere the HTML text
 * <span>AIFLedger.</span> used to render. Preserves navy serif + gold dots. */
export function PlatformWordmark({
  className = "h-6",
  height,
  alt = "AIFLedger",
  ...rest
}) {
  const src = useBrandField("wordmark_url", DEFAULT_ICON);
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} object-contain`}
      style={height ? { height, width: "auto" } : undefined}
      data-testid="platform-wordmark"
      {...rest}
    />
  );
}

/** The AIFLedger icon mark (dotted diamond). Used for the sidebar top
 * mark, favicon, PWA icon and email header. Falls back to the bundled
 * corpzo icon when no Owner logo has been configured. */
export function PlatformIcon({
  className = "h-9 w-9",
  width = 36,
  height = 36,
  alt = "AIFLedger",
  ...rest
}) {
  const src = useBrandField("logo_url", DEFAULT_ICON);
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={`${className} object-contain`}
      data-testid="platform-icon"
      {...rest}
    />
  );
}
